import { useEffect, useRef } from 'react';

import { logException } from '@atlaskit/editor-common/monitoring';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Snippet } from '@atlassian/editor-snippets-provider/types';

import type { SnippetsPlugin, SnippetsPluginOptions } from '../../snippetsPluginType';
import { registerConfluenceSnippets } from './registerConfluenceSnippets';

type UseRegisterSnippetsParams = {
	api: Parameters<SnippetsPlugin>[0]['api'];
	openTemplateGallery: SnippetsPluginOptions['openTemplateGallery'];
	snippetsProvider: SnippetsPluginOptions['snippetsProvider'];
};

export const useRegisterSnippets = ({
	api,
	openTemplateGallery,
	snippetsProvider,
}: UseRegisterSnippetsParams): void => {
	const snippetsPromiseRef = useRef<Promise<Snippet[] | undefined> | undefined>(undefined);
	const hasRegisteredRef = useRef(false);
	const isConfluenceSnippetsPluginEnabled = isExperimentEnabled(
		'platform_editor_snippets_plugin_confluence',
	);

	useEffect(() => {
		if (!isConfluenceSnippetsPluginEnabled) {
			return;
		}

		snippetsPromiseRef.current ??= snippetsProvider.getSnippets().catch((error) => {
			logException(error as Error, {
				location: 'editor-plugin-snippets/snippetsPlugin/getSnippets',
			});
			return undefined;
		});
		let isActive = true;
		void snippetsPromiseRef.current.then((snippets) => {
			if (isActive && snippets && !hasRegisteredRef.current) {
				hasRegisteredRef.current = true;
				api?.core?.actions.execute(({ tr }) =>
					registerConfluenceSnippets({ api, openTemplateGallery, snippets, tr }),
				);
			}
		});

		return () => {
			isActive = false;
		};
	}, [api, isConfluenceSnippetsPluginEnabled, openTemplateGallery, snippetsProvider]);
};
